<!--
  FormSection — a vertical block in an admin modal form.

  Presentational only: an optional `title` prop renders a <h4> section heading;
  the default slot holds the fields. The component owns the section's bottom
  spacing and the top-border divider drawn between consecutive sections, so that
  layout is defined ONCE here instead of being duplicated as cl-section /
  cl-section-title in every view (the drift this replaces).

  Pass `photo` for the centered photo block at the top of a form (replaces the
  per-view cl-photo-section).
-->
<template>
  <div class="form-section" :class="{ 'is-photo': photo }">
    <h4 v-if="title" class="form-section-title">
      {{ title }}
      <span v-if="optional" class="optional">(optional)</span>
    </h4>
    <slot />
  </div>
</template>

<script setup lang="ts">
withDefaults(
  defineProps<{
    /** Optional uppercase section heading rendered as an <h4>. */
    title?: string
    /** Append an "(optional)" tag to the heading. */
    optional?: boolean
    /** Center the section's content (used for the top photo block). */
    photo?: boolean
  }>(),
  { title: '', optional: false, photo: false },
)
</script>

<style scoped>
.form-section { margin-bottom: 1.25rem; }
/* Divider between consecutive sections — one definition for every form. */
.form-section + .form-section { border-top: 1px solid var(--border-subtle); padding-top: 1rem; }
.form-section.is-photo { display: flex; justify-content: center; }
.form-section-title {
  font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--text-muted); margin: 0 0 0.75rem;
}
</style>
